import { useState } from 'react';
import SafeIcon from './SafeIcon';
import { aiWorks } from '../store/seed';
import CampaignDetail from './CampaignDetail';
import CampaignWizard from './CampaignWizard';
import AddIdeaSheet from './AddIdeaSheet';
import { useApp, timeLabel } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import type { Campaign, Task } from '../store/seed';
import './ideas.css';
import {
  useDayFormat,
  campaignCounts,
  campaignDates,
  channelIconName,
  campaignStatusClass,
  localizeDemoDatesInVariables,
} from './campaignDisplay';

export default function IdeasPage() {
  const { translate, readOnlyProps, formatCount } = useApp();
  const { route, navigate } = useUi();
  const { campaigns, posts, tasks } = useWorkspaceData(route.workspaceId);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const formatDay = useDayFormat();
  const tab = route.id === 'tasks' ? 'tasks' : 'campaigns';
  const routeOpensOneCampaign = route.id && route.id !== 'tasks';
  const campaign = routeOpensOneCampaign
    ? campaigns.find((item) => item.id === route.id)
    : undefined;
  const activeTaskCount = tasks.filter((task) =>
    ['Queued', 'Working', 'Failed', 'Waiting'].includes(task.status)
  ).length;

  if (routeOpensOneCampaign) {
    if (!campaign)
      return (
        <div className="content-stack">
          <div className="panel calendar-empty">
            <SafeIcon name="FiSearch" />
            <p>{translate('This campaign does not exist in this workspace.')}</p>
            <button className="button" onClick={() => navigate({ page: 'ideas' })}>
              {translate('All campaigns')}
            </button>
          </div>
        </div>
      );
    return <CampaignDetail campaign={campaign} />;
  }

  const statusDisplayOrder: Campaign['status'][] = [
    'Needs your input',
    'Ideas ready',
    'Planning',
    'Active',
    'Draft',
    'Paused',
    'Finished',
  ];
  const sortedCampaigns = [...campaigns].sort(
    (first, second) =>
      statusDisplayOrder.indexOf(first.status) - statusDisplayOrder.indexOf(second.status)
  );

  return (
    <div className="content-stack ideas-page">
      <div className="page-title-row">
        <div>
          <h1>{translate('Ideas & campaigns')}</h1>
          <p className="subtitle">
            {translate('Plan creative work together, one good idea at a time.')}
          </p>
        </div>
        <div className="button-row">
          <button className="button" onClick={() => setIdeaOpen(true)} {...readOnlyProps}>
            <SafeIcon name="FiZap" />
            {translate('Add an idea')}
          </button>
          <button className="button primary" onClick={() => setWizardOpen(true)} {...readOnlyProps}>
            <SafeIcon name="FiPlus" />
            {translate('New campaign')}
          </button>
        </div>
      </div>
      <div className="segment ideas-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'campaigns'}
          className={tab === 'campaigns' ? 'selected' : ''}
          onClick={() => navigate({ page: 'ideas' })}
        >
          {translate('Campaigns')} <span className="tab-count">{campaigns.length}</span>
        </button>
        <button
          role="tab"
          aria-selected={tab === 'tasks'}
          className={tab === 'tasks' ? 'selected' : ''}
          onClick={() => navigate({ page: 'ideas', id: 'tasks' })}
        >
          {translate('AMY tasks')}{' '}
          {activeTaskCount > 0 && <span className="tab-count attention">{activeTaskCount}</span>}
        </button>
      </div>

      {tab === 'campaigns' && (
        <div className="campaign-list">
          {sortedCampaigns.map((item) => {
            const counts = campaignCounts(posts, item);
            const suggestedIdeaCount = item.ideas.filter(
              (idea) => idea.status === 'Suggested'
            ).length;
            return (
              <button
                key={item.id}
                className="panel campaign-card list-button"
                onClick={() => navigate({ page: 'ideas', id: item.id })}
                aria-label={translate('Open campaign: {name}', { name: item.name })}
              >
                <div className="campaign-card-head">
                  <div style={{ minWidth: 0 }}>
                    <h2>{item.name}</h2>
                    <div className="muted">
                      {campaignDates(item, formatDay, translate)} ·{' '}
                      {item.channels.map((channel) => (
                        <SafeIcon
                          key={channel}
                          name={channelIconName[channel] as 'FiInstagram'}
                          size={13}
                        />
                      ))}
                    </div>
                  </div>
                  <span className={`status ${campaignStatusClass[item.status]}`}>
                    {translate(item.status)}
                  </span>
                </div>
                {item.status === 'Needs your input' && item.question && (
                  <p className="campaign-question">
                    <SafeIcon name="FiHelpCircle" size={14} /> {translate(item.question)}
                  </p>
                )}
                <div className="campaign-meters">
                  <span>{formatCount(counts.total, 'post')}</span>
                  {counts.published > 0 && (
                    <span className="muted">
                      {translate('{n} published', { n: counts.published })}
                    </span>
                  )}
                  {counts.scheduled > 0 && (
                    <span className="muted">
                      {translate('{n} scheduled', { n: counts.scheduled })}
                    </span>
                  )}
                  {counts.review > 0 && (
                    <span className="status review">
                      {translate('{n} to review', { n: counts.review })}
                    </span>
                  )}
                  {suggestedIdeaCount > 0 && (
                    <span className="status ready">
                      {translate('{n} new ideas', { n: suggestedIdeaCount })}
                    </span>
                  )}
                </div>
                {counts.total > 0 && (
                  <div className="campaign-bar" aria-hidden="true">
                    <span className="seg published" style={{ flex: counts.published }} />
                    <span className="seg scheduled" style={{ flex: counts.scheduled }} />
                    <span className="seg review" style={{ flex: counts.review + counts.input }} />
                    <span className="seg working" style={{ flex: counts.working }} />
                  </div>
                )}
              </button>
            );
          })}
          {campaigns.length === 0 && (
            <div className="panel calendar-empty">
              <SafeIcon name="FiZap" />
              <p>{translate('No campaigns yet. Start one and AMY suggests the first ideas.')}</p>
            </div>
          )}
        </div>
      )}

      {tab === 'tasks' && <TaskList tasks={tasks} />}
      {ideaOpen && <AddIdeaSheet onClose={() => setIdeaOpen(false)} />}
      {wizardOpen && (
        <CampaignWizard
          onClose={() => setWizardOpen(false)}
          onSaved={(id) => {
            setWizardOpen(false);
            navigate({ page: 'ideas', id });
          }}
        />
      )}
    </div>
  );
}

type TaskFilter = 'all' | 'active' | 'Ready' | 'Failed';
const MINIMUM_TEXT_ANSWER_LENGTH = 3;

function taskMatchesFilter(task: Task, filter: TaskFilter) {
  if (filter === 'all') return task.status !== 'Cancelled';
  if (filter === 'active') return ['Queued', 'Working', 'With agency'].includes(task.status);
  if (filter === 'Failed') return task.status === 'Failed' || task.status === 'Waiting';
  return task.status === filter;
}

export function TaskList({ tasks, compact = false }: { tasks: Task[]; compact?: boolean }) {
  const { translate, readOnlyProps, language } = useApp();
  const { route, navigate, openChat, actor, notify } = useUi();
  const { commands } = useDemo();
  const { campaigns, conversations, aiConnection } = useWorkspaceData(route.workspaceId);
  const aiAvailable = aiWorks(aiConnection);
  const [filter, setFilter] = useState<TaskFilter>('all');
  const shownTasks = tasks.filter((task) => taskMatchesFilter(task, filter));
  const statusIcon: Record<Task['status'], string> = {
    Queued: 'FiClock',
    Working: 'FiLoader',
    Ready: 'FiCheckCircle',
    Failed: 'FiAlertTriangle',
    Cancelled: 'FiXCircle',
    Waiting: 'FiHelpCircle',
    'With agency': 'FiUsers',
  };
  const statusClass: Record<Task['status'], string> = {
    Queued: 'neutral',
    Working: 'ready',
    Ready: 'scheduled',
    Failed: 'input',
    Cancelled: 'neutral',
    Waiting: 'review',
    'With agency': 'neutral',
  };
  return (
    <div className="task-list">
      {!compact && (
        <>
          <p className="muted">
            {translate(
              'Everything you or your team asked AMY to do, from chat and campaigns. Demo: AMY’s work is simulated.'
            )}
          </p>
          <div className="filter-row">
            {(
              [
                ['all', 'All'],
                ['active', 'In progress'],
                ['Ready', 'Ready'],
                ['Failed', 'Needs you'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                className={`channel-chip ${filter === id ? 'selected' : ''}`}
                aria-pressed={filter === id}
                onClick={() => setFilter(id)}
              >
                {translate(label)}
              </button>
            ))}
          </div>
        </>
      )}
      {shownTasks.length === 0 && <p className="muted">{translate('No tasks here.')}</p>}
      {shownTasks.map((task) => {
        const campaign = campaigns.find((candidate) => candidate.id === task.campaignId);
        const conversation = conversations.find(
          (candidate) => candidate.id === task.conversationId
        );
        const draftPostIds = task.result?.postIds;
        const aiCannotRunThisTask =
          !aiAvailable && (task.status === 'Queued' || task.status === 'Working');
        return (
          <div key={task.id} className={`panel task-row task-${task.status.toLowerCase()}`}>
            <SafeIcon name={statusIcon[task.status] as 'FiClock'} />
            <div className="task-body">
              <strong>
                {task.showAsWritten ? task.title : translate(task.title, task.variables)}
              </strong>
              <div className="muted small-print">
                {translate('Asked by {name}', { name: task.by })} ·{' '}
                {timeLabel(task.at, language, translate)}
                {conversation && !compact && (
                  <>
                    {' '}
                    ·{' '}
                    <button
                      className="link-button"
                      onClick={() => openChat(undefined, conversation.id)}
                    >
                      {conversation.showAsWritten
                        ? conversation.title
                        : translate(conversation.title)}
                    </button>
                  </>
                )}
                {campaign && !compact && (
                  <>
                    {' '}
                    ·{' '}
                    <button
                      className="link-button"
                      onClick={() => navigate({ page: 'ideas', id: campaign.id })}
                    >
                      {campaign.name}
                    </button>
                  </>
                )}
              </div>
              {task.plannedFor && task.status === 'Queued' && (
                <div className="muted small-print">
                  {translate(
                    'Planned for {day}',
                    localizeDemoDatesInVariables({ day: `${task.plannedFor} Nov` }, language)
                  )}
                </div>
              )}
              <TaskDetails task={task} />
            </div>
            <span className={`status ${statusClass[task.status]}`}>
              {aiCannotRunThisTask ? translate('Waiting for AI') : translate(task.status)}
            </span>
            <div className="button-row task-actions">
              {draftPostIds?.map((id, index) => (
                <button
                  key={id}
                  className="button small"
                  onClick={() => navigate({ page: 'post', id })}
                >
                  {draftPostIds.length > 1
                    ? translate('Draft {n}', { n: index + 1 })
                    : translate('Open draft')}
                </button>
              ))}
              {task.result?.ideaCount && campaign && (
                <button
                  className="button small"
                  onClick={() => navigate({ page: 'ideas', id: campaign.id })}
                >
                  {translate('See ideas')}
                </button>
              )}
              {task.status === 'Queued' && task.plannedFor && (
                <button
                  className="button small"
                  onClick={() => commands.startTask(task.id)}
                  {...readOnlyProps}
                >
                  {translate('Start now')}
                </button>
              )}
              {task.status === 'Failed' && (
                <button
                  className="button small primary"
                  onClick={() => commands.startTask(task.id)}
                  {...readOnlyProps}
                >
                  <SafeIcon name="FiRotateCw" size={14} />
                  {translate('Retry')}
                </button>
              )}
              {(task.status === 'Failed' || task.status === 'Waiting') && (
                <button
                  className="button small"
                  onClick={() => {
                    commands.handToAgency(task.id, actor);
                    notify(translate('Handed to Hakusana. They reply in the conversation.'));
                  }}
                  {...readOnlyProps}
                >
                  <SafeIcon name="FiUsers" size={14} />
                  {translate('Hand to agency')}
                </button>
              )}
              {['Queued', 'Failed', 'Waiting', 'With agency'].includes(task.status) && (
                <button
                  className="button small ghost"
                  onClick={() => commands.cancelTask(task.id)}
                  {...readOnlyProps}
                >
                  {translate('Cancel task')}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function TaskDetails({ task, compact = false }: { task: Task; compact?: boolean }) {
  const { translate, readOnlyProps, readOnly, language } = useApp();
  const { actor, notify, role } = useUi();
  const { commands } = useDemo();
  const [answer, setAnswer] = useState('');
  const [doneNote, setDoneNote] = useState('');
  const staffHandling = task.status === 'With agency' && role === 'agency';
  const answerIsLongEnough = answer.trim().length >= MINIMUM_TEXT_ANSWER_LENGTH;
  const doneNoteIsLongEnough = doneNote.trim().length >= MINIMUM_TEXT_ANSWER_LENGTH;
  return (
    <div className="task-details">
      {task.interpretation && (
        <p className="task-line">
          <span className="muted">{translate('AMY understood')}:</span>{' '}
          {translate(
            task.interpretation.text,
            localizeDemoDatesInVariables(task.interpretation.variables, language)
          )}
        </p>
      )}
      {task.materials && task.materials.length > 0 && (
        <div className="chat-attachments">
          {task.materials.map((name) => (
            <span key={name} className="attachment-chip">
              <SafeIcon name="FiPaperclip" size={12} />
              {name}
            </span>
          ))}
        </div>
      )}
      {(task.status === 'Waiting' || staffHandling) && task.question && (
        <div className="task-question">
          <strong>
            <SafeIcon name="FiHelpCircle" size={14} />{' '}
            {translate(staffHandling ? 'Answer for the client' : 'Waiting for your answer')}
          </strong>
          <p>
            {translate(task.question, localizeDemoDatesInVariables(task.questionVars, language))}
          </p>
          {task.choices ? (
            <div className="button-row">
              {task.choices.map((choice, index) => (
                <button
                  key={choice.value}
                  className={`button small ${index === 0 ? 'primary' : ''}`}
                  disabled={readOnly}
                  onClick={() => {
                    commands.answerTask(task.id, choice.value, actor);
                    notify(translate('Thanks. AMY continues with your answer.'));
                  }}
                >
                  {translate(choice.label)}
                </button>
              ))}
            </div>
          ) : (
            !compact && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!answerIsLongEnough) return;
                  commands.answerTask(task.id, answer.trim(), actor);
                  setAnswer('');
                  notify(translate('Thanks. AMY continues with your answer.'));
                }}
              >
                <textarea
                  className="text-field"
                  rows={2}
                  value={answer}
                  onChange={(event) => setAnswer(event.target.value)}
                  aria-label={translate('Your answer')}
                  {...readOnlyProps}
                />
                <button
                  className="button small primary"
                  type="submit"
                  disabled={readOnly || !answerIsLongEnough}
                >
                  <SafeIcon name="FiSend" size={14} />
                  {translate('Send answer')}
                </button>
              </form>
            )
          )}
        </div>
      )}
      {staffHandling && !compact && (
        <form
          className="task-question"
          onSubmit={(event) => {
            event.preventDefault();
            if (!doneNoteIsLongEnough) return;
            commands.completeHandedTask(task.id, doneNote.trim(), actor);
            setDoneNote('');
            notify(translate('Task done. The client sees your note.'));
          }}
        >
          <strong>
            <SafeIcon name="FiCheckCircle" size={14} /> {translate('Or finish it yourself')}
          </strong>
          <textarea
            className="text-field"
            rows={2}
            value={doneNote}
            onChange={(event) => setDoneNote(event.target.value)}
            aria-label={translate('What you did')}
            placeholder={translate(
              'For example: I called Anna and added the posts to the calendar.'
            )}
          />
          <button className="button small" type="submit" disabled={!doneNoteIsLongEnough}>
            <SafeIcon name="FiCheck" size={14} />
            {translate('Mark as done')}
          </button>
        </form>
      )}
      {task.answer && (
        <p className="task-line">
          <span className="muted">{translate('Your answer')}:</span> {task.answer}
        </p>
      )}
      {task.status === 'Failed' && task.reason && (
        <p className="task-reason">
          {translate(task.reason, localizeDemoDatesInVariables(task.result?.variables, language))}
        </p>
      )}
      {task.status === 'Ready' && task.result?.text && (
        <p className="muted task-result">
          {translate(
            task.result.text,
            localizeDemoDatesInVariables(task.result.variables, language)
          )}
        </p>
      )}
      {task.status === 'Ready' && task.reason && (
        <p className="task-reason">
          {translate(task.reason, localizeDemoDatesInVariables(task.result?.variables, language))}
        </p>
      )}
      {task.nextStep && !['Ready', 'Cancelled'].includes(task.status) && (
        <p className="task-line">
          <span className="muted">{translate('Next step')}:</span> {translate(task.nextStep)}
        </p>
      )}
    </div>
  );
}
